import cloneDeep from 'clone-deep';
import Logger from './logger/logger';
import Localizations from './localizations';
import { clearStorage } from './storageManager';
import { loadScript, loadStyleSheet } from './utilities';
import {
  registerCobrowseChannelEvents,
  registerChatChannelEvents,
  dispatchChannelsListeners,
} from './eventRegister';
import track from './altocloud';

let cobrowseAPI;

function openChat() {
  Logger.info('chat.js', 'openChat()', 'Opening chat...');
  const { userDataExtensions, form, overrides } = window.channelsConfig.chat;

  const openConfig = {
    form: {
      autoSubmit: false,
    },
  };
  if (overrides) {
    overrides.forEach(override => {
      override.paths.forEach(path => {
        if (window.location.href.includes(path)) {
          openConfig.userData = override.config.userData;
          if (override.config.surveyLink) {
            // TODO: this should be set using setState, but not until a future major update, as it would break compatibility with older versions
            document.cookie = `chatSurveyLink=${override.config.surveyLink}`;
          }
          // TODO: remove the right side of the conditional once myT fixes the "enableTranscipt" typo.
          window.channelsConfig.chat.enableTranscript =
            override.config.enableTranscript || override.config.enableTranscipt;
        }
      });
    });
  }

  if (form) {
    form.inputs.forEach(input => {
      if (input.value) {
        if (typeof input.value === 'string') {
          openConfig.form[input.name] = input.value;
        } else if (typeof input.value === 'function') {
          openConfig.form[input.name] = input.value();
        }
      }
    });
  }
  if (userDataExtensions) {
    userDataExtensions.forEach(extension => {
      if (window.location.href === extension.path) {
        openConfig.userData = { ...(openConfig.userData || {}), ...extension.data };
      }
    });
  }
  window._genesys.widgets.bus
    .command('WebChat.open', openConfig)
    .done(() => {
      // WebChat opened successfully
      Logger.info('chat.js', 'openChat()', 'Chat opened successfully.');
      // set focus to first input element in chat form:
      const firstName = document.getElementsByClassName('cx-input')[0];
      if (firstName) firstName.focus();
    })
    .fail(error => {
      // WebChat isn't open or no active chat session
      Logger.error('chat.js', 'openChat()', `Failed to open chat: ${error}`);
    });
}

function openCobrowse() {
  const isCobrowseActive = document.getElementsByClassName('gcb-toolbar').length;
  if (!isCobrowseActive) {
    window.dispatchEvent(
      new CustomEvent('cobrowse.linkClicked', {
        detail: {
          subject: 'phone_cobrowse',
        },
      }),
    );
    setTimeout(() => {
      const CXBus = window._genesys.widgets.bus;
      CXBus.command('CoBrowse.start')
        .done(() => {
          Logger.info('channels.js', 'registerClickStartCobrowseEvent()', 'voice cobrowse started');
        })
        .fail(() => {
          Logger.error(
            'channels.js',
            'registerClickStartCobrowseEvent()',
            'failed to start voice cobrowse',
          );
        });
    }, 150);
  }
}

function validateChatConfig() {
  let valid = true;
  const { chat: chatConfig } = window.channelsConfig;
  if (!chatConfig) {
    Logger.error('channels.js', 'validateConfigs()', 'Chat config is not defined!');
    valid = false;
  } else if (!chatConfig.userData) {
    Logger.error('channels.js', 'validateConfigs()', 'Chat config is missing userData property!');
    valid = false;
  }

  return valid;
}

function validateCobrowseConfig() {
  let valid = true;
  const { cobrowse: cobrowseConfig } = window.channelsConfig;
  if (!cobrowseConfig) {
    Logger.error('channels.js', 'validateConfigs()', 'Cobrowse config is not defined!');
    valid = false;
  } else {
    if (!cobrowseConfig.src) {
      Logger.error('channels.js', 'validateConfigs()', 'Cobrowse config is missing src property!');
      valid = false;
    }
    if (!cobrowseConfig.url) {
      Logger.error('channels.js', 'validateConfigs()', 'Cobrowse config is missing url property!');
      valid = false;
    }
    if (!cobrowseConfig.triggerClass) {
      Logger.error(
        'channels.js',
        'validateConfigs()',
        'Cobrowse config is missing triggerClass property!',
      );
      valid = false;
    }
  }
  return valid;
}

function setConfigs(channels) {
  const { chat: chatConfig, cobrowse: cobrowseConfig } = window.channelsConfig;
  if (!window._genesys)
    window._genesys = {
      widgets: {
        main: {
          debug: Logger.isDebugEnabled(),
          theme: 'light',
          lang: 'en',
          downloadGoogleFont: false,
          plugins: [],
          i18n: Localizations,
          // TODO: Uncomment this for a future major release - not compatible with older versions
          // cookieOptions: {
          //   domain: window.location.hostname,
          //   path: '/',
          // },
        },
        onReady: () => registerChatChannelEvents(chatConfig.userData.userId || ''),
      },
      onReady: [
        APIs => {
          if (APIs.cobrowse) {
            registerCobrowseChannelEvents(APIs.cobrowse);
          }
        },
      ],
    };
  if (!window._gt) window._gt = [];
  if (channels.includes('chat') && validateChatConfig()) {
    // TODO: remove this once myT fixes the "enableTranscipt" typo on their end.
    window.channelsConfig.chat.enableTranscript =
      chatConfig.enableTranscript || chatConfig.enableTranscipt;
    if (chatConfig.localization) {
      window._genesys.widgets.main.i18n.en.webchat = {
        ...Localizations.en.webchat,
        ...chatConfig.localization,
      };
    }
    window._genesys.widgets.main.preload = chatConfig.triggerClass ? [] : ['webchat'];
    window._genesys.widgets.webchat = {
      apikey: '', // Used for Apigee service only
      dataURL: process.env.GMS_URL,
      userData: chatConfig.userData,
      actionsMenu: false,
      ajaxTimeout: 15000,
      autoinvite: {
        enabled: false,
      },
      chatButton: {
        enabled: !chatConfig.triggerClass,
        openDelay: 0,
        effectDuration: 0,
        hideDuringInvite: true,
      },
    };
    if (chatConfig.form) {
      const formCopy = cloneDeep(chatConfig.form);
      chatConfig.form.inputs.forEach((input, index) => {
        if (input.value && typeof input.value === 'function') {
          formCopy.inputs[index].value = input.value();
        }
      });
      window._genesys.widgets.webchat.form = formCopy;
    }
    if (chatConfig.surveyLink) {
      // TODO: this should be set using setState, but not until a future major update, as it would break compatibility with older versions
      document.cookie = `chatSurveyLink=${chatConfig.surveyLink}`;
    }
    if (chatConfig.enableAltocloud) {
      track();
    }
  }

  if (channels.includes('cobrowse') && validateCobrowseConfig()) {
    if (cobrowseConfig.localization) {
      window._genesys.widgets.main.i18n.en.cobrowse = {
        ...Localizations.en.cobrowse,
        ...cobrowseConfig.localization,
      };
    }
    if (channels.includes('chat') && window._genesys.widgets.webchat) {
      window._genesys.widgets.webchat.actionsMenu = true;
    }
    window._genesys.widgets.cobrowse = {
      css: {
        server: false,
        browser: true,
      },
      disableWebSockets: false,
      src: cobrowseConfig.src,
      url: cobrowseConfig.url,
    };
  }
  // TODO: this should be handled with getState/setState, but not until a future major update, as it would break compatibility with older versions
  const storedConfig = JSON.parse(window.localStorage.getItem('genesysConfig')) || {};
  if (!window._genesys.widgets.webchat) {
    window._genesys.widgets.webchat = storedConfig.webchat;
  }
  if (!window._genesys.widgets.cobrowse) {
    window._genesys.widgets.cobrowse = storedConfig.cobrowse;
  }
  window.localStorage.setItem('genesysConfig', JSON.stringify(window._genesys.widgets));
}

function loadGenesysWidgets() {
  return new Promise(resolve => {
    loadStyleSheet(`${process.env.GENESYS_CDN_ENDPOINT}/widgets.min.css`);
    loadScript(`${process.env.GENESYS_CDN_ENDPOINT}/widgets.min.js`, () => {
      window.CXBus.configure({
        debug: Logger.isDebugEnabled(),
      });
      resolve();
    });
    Logger.info('chat.js', 'loadChat', 'Chat loading done.');
  });
}

function setupOpenListener(channels) {
  window.document.addEventListener('click', ({ target: { classList } }) => {
    if (channels.includes('chat')) {
      const { triggerClass } = window.channelsConfig.chat;
      if (triggerClass && classList.contains(triggerClass)) {
        openChat();
      }
    }
    if (channels.includes('cobrowse')) {
      const { triggerClass } = window.channelsConfig.cobrowse;
      if (triggerClass && classList.contains(triggerClass)) {
        openCobrowse();
      }
    }
  });
}

async function init(channels) {
  if (window._genesys == null && channels && channels.length > 0) {
    Logger.info('channels.js', 'init()', 'Initializing channels...');
    dispatchChannelsListeners();
    setConfigs(channels);
    await loadGenesysWidgets();
    setupOpenListener(channels);
  }
}

function clearAllWebChatCookies() {
  clearStorage();
  // exit any active cobrowse session
  if (cobrowseAPI && document.getElementsByClassName('gcb-toolbar').length) {
    cobrowseAPI.exitSession();
  }
}

function extendUserData(data) {
  if (!window.channelsConfig || !window.channelsConfig.chat) {
    Logger.error('channels.js', 'extendUserData()', 'Chat config is not defined!');
    return;
  }
  if (!window.channelsConfig.chat.userDataExtensions) {
    window.channelsConfig.chat.userDataExtensions = [];
  }
  const newExtension = {
    path: window.location.href,
    data,
  };
  window.channelsConfig.chat.userDataExtensions = window.channelsConfig.chat.userDataExtensions.filter(
    extension => extension.path !== newExtension.path,
  );
  window.channelsConfig.chat.userDataExtensions.push(newExtension);
}

export { init, clearAllWebChatCookies, extendUserData };
